import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { portalFetch } from '../lib/supabase';

const fmt     = n => `$${parseFloat(n||0).toFixed(2)}`;
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—';

const REWARD_THRESHOLD = 10.00;

function Card({ children, style }) {
  return <div style={{ background:'white', border:'1px solid var(--border)', borderRadius:12, ...style }}>{children}</div>;
}

function Toggle({ checked, onChange, label }) {
  return (
    <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
      <div onClick={onChange} style={{
        width:44, height:24, borderRadius:12, position:'relative', cursor:'pointer',
        background: checked ? 'var(--orange)' : '#D1D5DB', transition:'background 0.2s', flexShrink:0,
      }}>
        <div style={{
          position:'absolute', top:3, left: checked ? 23 : 3,
          width:18, height:18, borderRadius:9, background:'white',
          transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </div>
      {label && <span style={{ fontSize:'0.875rem', fontWeight:600, color:'var(--dark)' }}>{label}</span>}
    </label>
  );
}

function ProgressBar({ value, max }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  return (
    <div style={{ background:'#F0E8E0', borderRadius:99, height:6, overflow:'hidden' }}>
      <div style={{ background: pct >= 100 ? '#059669' : 'var(--orange)', width:`${pct}%`, height:'100%', borderRadius:99, transition:'width 0.3s' }} />
    </div>
  );
}

export default function Loyalty() {
  const { restaurant } = useAuth();
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);
  const [settings, setSettings]   = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const load = useCallback(async () => {
    if (!restaurant?.id) return;
    setLoading(true);
    const d = await portalFetch('/loyalty');
    if (d) {
      setData(d);
      setSettings({ ...d.settings });
    }
    setLoading(false);
  }, [restaurant?.id]);

  useEffect(() => { load(); }, [load]);

  const save = async (updates) => {
    setSaving(true);
    const d = await portalFetch('/loyalty', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    if (d) {
      setSettings(d.settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
    setSaving(false);
  };

  if (loading) return <div style={{ padding:'3rem', textAlign:'center', color:'var(--muted)' }}>Loading...</div>;

  const { summary={}, topCustomers=[], nearReward=[], recentTransactions=[] } = data || {};
  const s           = settings || {};
  const cashbackPct = parseFloat(s.cashback_pct || 10);
  const bonusActive = parseFloat(s.bonus_multiplier || 1) > 1
                   && (!s.bonus_expires_at || new Date(s.bonus_expires_at) > new Date());
  const effectivePct = bonusActive
    ? parseFloat((cashbackPct * parseFloat(s.bonus_multiplier)).toFixed(1))
    : cashbackPct;

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'1.5rem' }}>
        <div>
          <h1 style={{ fontFamily:'Fraunces, serif', fontSize:'1.8rem', fontWeight:900, margin:0 }}>
            Loyalty & Rewards
          </h1>
          <p style={{ color:'var(--muted)', fontSize:'0.875rem', marginTop:4 }}>
            Customers earn <strong>{effectivePct}% back</strong> on every order
            {bonusActive && <span style={{ color:'var(--orange)', fontWeight:700 }}> (bonus active 🎉)</span>}
            {' '}· Redeem when they hit ${REWARD_THRESHOLD} cashback
          </p>
        </div>
        <Toggle
          checked={s.active !== false}
          onChange={() => save({ active: !s.active })}
          label={s.active !== false ? 'Active' : 'Paused'}
        />
      </div>

      {/* Stat cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'1rem', marginBottom:'1.5rem' }}>
        {[
          { icon:'👥', label:'Members',               value: summary.totalMembers || 0,       color:'var(--dark)'   },
          { icon:'🎁', label:'Reward Ready',           value: summary.rewardReady || 0,        color:'var(--orange)' },
          { icon:'💰', label:'Cashback Earned (30d)',  value: fmt(summary.cashbackEarned30d),  color:'var(--dark)'   },
          { icon:'🎉', label:'Rewards Redeemed (30d)', value: summary.rewardsRedeemed || 0,   color:'var(--orange)' },
        ].map(({ icon, label, value, color }) => (
          <Card key={label} style={{ padding:'1.2rem 1.4rem' }}>
            <div style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'0.4rem' }}>
              {icon} {label}
            </div>
            <div style={{ fontFamily:'Fraunces, serif', fontSize:'1.8rem', fontWeight:900, color }}>{value}</div>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:'1.2rem', borderBottom:'1px solid var(--border)' }}>
        {[['overview','Overview'],['members','Members'],['settings','Settings']].map(([tab, label]) => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            padding:'0.5rem 1rem', border:'none', background:'none', cursor:'pointer',
            fontSize:'0.875rem', fontWeight:600, fontFamily:'inherit',
            color: activeTab===tab ? 'var(--orange)' : 'var(--muted)',
            borderBottom: activeTab===tab ? '2px solid var(--orange)' : '2px solid transparent',
            marginBottom:-1,
          }}>{label}</button>
        ))}
      </div>

      {/* OVERVIEW */}
      {activeTab === 'overview' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
          <Card>
            <div style={{ padding:'1rem 1.2rem', borderBottom:'1px solid var(--warm)', fontWeight:700, fontSize:'0.9rem', color:'var(--dark)' }}>
              🔥 Close to their $10 reward
            </div>
            {nearReward.length === 0 ? (
              <div style={{ padding:'1.5rem', textAlign:'center', color:'var(--muted)', fontSize:'0.875rem' }}>
                No members near their reward yet
              </div>
            ) : nearReward.map(a => {
              const bal = parseFloat(a.cashback_balance || 0);
              return (
                <div key={a.id} style={{ padding:'0.75rem 1.2rem', borderBottom:'1px solid var(--warm)', display:'flex', alignItems:'center', gap:'0.75rem' }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:600, fontSize:'0.875rem', color:'var(--dark)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {a.customer_name || a.customer_email}
                    </div>
                    <ProgressBar value={bal} max={REWARD_THRESHOLD} />
                    <div style={{ fontSize:'0.72rem', color:'var(--muted)', marginTop:3 }}>
                      {fmt(bal)} of {fmt(REWARD_THRESHOLD)} cashback
                    </div>
                  </div>
                  <div style={{ fontSize:'0.8rem', fontWeight:700, color:'var(--orange)', whiteSpace:'nowrap' }}>
                    {fmt(REWARD_THRESHOLD - bal)} to go
                  </div>
                </div>
              );
            })}
          </Card>

          <Card>
            <div style={{ padding:'1rem 1.2rem', borderBottom:'1px solid var(--warm)', fontWeight:700, fontSize:'0.9rem', color:'var(--dark)' }}>
              📋 Recent activity
            </div>
            {recentTransactions.length === 0 ? (
              <div style={{ padding:'1.5rem', textAlign:'center', color:'var(--muted)', fontSize:'0.875rem' }}>
                No loyalty activity yet
              </div>
            ) : recentTransactions.slice(0, 8).map(t => {
              const isEarn   = t.type === 'earn';
              const isRedeem = t.type === 'redeem';
              const amt      = Math.abs(parseFloat(t.cashback_amount || 0));
              return (
                <div key={t.id} style={{ padding:'0.65rem 1.2rem', borderBottom:'1px solid var(--warm)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div>
                    <div style={{ fontSize:'0.8rem', fontWeight:600, color:'var(--dark)' }}>
                      {isEarn ? '⭐ Cashback earned' : isRedeem ? '🎁 Reward redeemed' : '✏️ Adjustment'}
                    </div>
                    <div style={{ fontSize:'0.72rem', color:'var(--muted)' }}>
                      {fmtDate(t.created_at)} · {t.channel || 'online'}
                    </div>
                  </div>
                  <div style={{ fontWeight:700, fontSize:'0.875rem', color: isEarn ? '#065F46' : isRedeem ? 'var(--orange)' : 'var(--muted)' }}>
                    {isEarn ? '+' : '-'}{fmt(amt)}
                  </div>
                </div>
              );
            })}
          </Card>
        </div>
      )}

      {/* MEMBERS */}
      {activeTab === 'members' && (
        <Card>
          <div style={{ padding:'0.75rem 1.2rem 0', display:'grid', gridTemplateColumns:'2.5fr 1fr 1fr 1fr 1fr', gap:'0.5rem', borderBottom:'1px solid var(--border)', background:'var(--warm)', borderRadius:'12px 12px 0 0' }}>
            {['Member','Cashback Balance','Lifetime Spend','Orders','Status'].map(h => (
              <div key={h} style={{ fontSize:'0.72rem', fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.08em', padding:'0 0 0.75rem' }}>{h}</div>
            ))}
          </div>
          {topCustomers.length === 0 ? (
            <div style={{ padding:'3rem', textAlign:'center', color:'var(--muted)', fontSize:'0.875rem' }}>
              No loyalty members yet.
            </div>
          ) : topCustomers.map(a => {
            const bal     = parseFloat(a.cashback_balance || 0);
            const isReady = bal >= REWARD_THRESHOLD;
            return (
              <div key={a.id} style={{ display:'grid', gridTemplateColumns:'2.5fr 1fr 1fr 1fr 1fr', gap:'0.5rem', padding:'0.75rem 1.2rem', borderBottom:'1px solid var(--warm)', alignItems:'center', fontSize:'0.875rem' }}>
                <div>
                  <div style={{ fontWeight:600, color:'var(--dark)' }}>{a.customer_name || '—'}</div>
                  <div style={{ fontSize:'0.75rem', color:'var(--muted)' }}>{a.customer_email}</div>
                </div>
                <div>
                  <div style={{ fontWeight:700, color: isReady ? '#065F46' : 'var(--dark)', marginBottom:3 }}>{fmt(bal)}</div>
                  <ProgressBar value={bal} max={REWARD_THRESHOLD} />
                </div>
                <div style={{ color:'var(--mid)' }}>{fmt(a.lifetime_spend)}</div>
                <div style={{ color:'var(--mid)' }}>{a.total_orders || 0}</div>
                <div>
                  <span style={{
                    fontSize:'0.7rem', fontWeight:700, padding:'0.2rem 0.5rem', borderRadius:10,
                    background: isReady ? '#D1FAE5' : '#FEF3C7',
                    color:      isReady ? '#065F46' : '#92400E',
                  }}>
                    {isReady ? '🎁 Reward ready' : '⭐ Earning'}
                  </span>
                </div>
              </div>
            );
          })}
        </Card>
      )}

      {/* SETTINGS */}
      {activeTab === 'settings' && (
        <div style={{ maxWidth:520 }}>
          <Card style={{ padding:'1.5rem' }}>
            <h3 style={{ margin:'0 0 0.4rem', fontSize:'1rem', fontWeight:700, color:'var(--dark)' }}>
              Cashback Rate
            </h3>
            <p style={{ margin:'0 0 1.2rem', fontSize:'0.875rem', color:'var(--muted)' }}>
              Set the percentage customers earn back on every order. When they accumulate $10 cashback, they get a $10 reward automatically.
            </p>
            <div style={{ marginBottom:'1.5rem' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'1rem' }}>
                <div style={{ position:'relative', flex:1 }}>
                  <input
                    type="number" min="1" max="30" step="1"
                    value={s.cashback_pct || 10}
                    onChange={e => setSettings(prev => ({ ...prev, cashback_pct: e.target.value }))}
                    style={{ width:'100%', padding:'0.75rem 2.5rem 0.75rem 1rem', border:'2px solid var(--orange)', borderRadius:10, fontSize:'1.5rem', fontWeight:700, fontFamily:'inherit', outline:'none', boxSizing:'border-box', color:'var(--dark)' }}
                  />
                  <span style={{ position:'absolute', right:'0.75rem', top:'50%', transform:'translateY(-50%)', fontSize:'1.2rem', fontWeight:700, color:'var(--orange)' }}>%</span>
                </div>
                <div style={{ fontSize:'0.875rem', color:'var(--muted)', lineHeight:1.5 }}>
                  <strong style={{ color:'var(--dark)' }}>back on every order</strong><br/>
                  A $50 order earns {fmt(50 * (parseFloat(s.cashback_pct || 10) / 100))} cashback
                </div>
              </div>
              <div style={{ marginTop:'1rem', display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'0.5rem' }}>
                {[20, 50, 100].map(spend => {
                  const earned = parseFloat((spend * (parseFloat(s.cashback_pct || 10) / 100)).toFixed(2));
                  const ordersToReward = Math.ceil(REWARD_THRESHOLD / earned);
                  return (
                    <div key={spend} style={{ padding:'0.65rem', background:'#FFF8F3', border:'1px solid var(--border)', borderRadius:8, textAlign:'center' }}>
                      <div style={{ fontSize:'0.72rem', color:'var(--muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em' }}>${spend} order</div>
                      <div style={{ fontSize:'1rem', fontWeight:700, color:'var(--orange)', margin:'0.25rem 0' }}>+{fmt(earned)}</div>
                      <div style={{ fontSize:'0.7rem', color:'var(--muted)' }}>{ordersToReward} orders → $10 reward</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ marginBottom:'1.5rem', padding:'1rem', background:'#FFF8F3', border:'1px solid var(--border)', borderRadius:10 }}>
              <div style={{ fontWeight:700, color:'var(--dark)', marginBottom:'0.75rem', fontSize:'0.9rem' }}>🎉 Bonus Campaign</div>
              <div style={{ display:'flex', gap:'0.75rem' }}>
                <div style={{ flex:1 }}>
                  <label style={{ fontSize:'0.75rem', color:'var(--muted)', fontWeight:600, display:'block', marginBottom:4 }}>Multiplier</label>
                  <select
                    value={s.bonus_multiplier || 1}
                    onChange={e => setSettings(prev => ({ ...prev, bonus_multiplier: parseFloat(e.target.value) }))}
                    style={{ width:'100%', padding:'0.5rem', border:'1.5px solid var(--border)', borderRadius:8, fontSize:'0.875rem', background:'white', fontFamily:'inherit' }}
                  >
                    <option value="1">1× — Normal rate</option>
                    <option value="1.5">1.5× — 50% bonus</option>
                    <option value="2">2× — Double cashback</option>
                    <option value="3">3× — Triple cashback</option>
                  </select>
                </div>
                <div style={{ flex:1 }}>
                  <label style={{ fontSize:'0.75rem', color:'var(--muted)', fontWeight:600, display:'block', marginBottom:4 }}>Ends on</label>
                  <input
                    type="datetime-local"
                    value={s.bonus_expires_at ? new Date(s.bonus_expires_at).toISOString().slice(0,16) : ''}
                    onChange={e => setSettings(prev => ({ ...prev, bonus_expires_at: e.target.value ? new Date(e.target.value).toISOString() : null }))}
                    style={{ width:'100%', padding:'0.5rem', border:'1.5px solid var(--border)', borderRadius:8, fontSize:'0.875rem', fontFamily:'inherit' }}
                  />
                </div>
              </div>
            </div>

            <button
              onClick={() => save(s)}
              disabled={saving}
              style={{ width:'100%', padding:'0.75rem', background:'var(--orange)', color:'white', border:'none', borderRadius:8, fontSize:'0.95rem', fontWeight:700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily:'inherit', opacity: saving ? 0.7 : 1 }}
            >
              {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Settings'}
            </button>
          </Card>
        </div>
      )}
    </div>
  );
}
